// src/pages/withdraw.tsx
import { useState } from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

import TransactionItem from "@/components/transaction-item";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

import {
  Database,
  Wallet,
  Info,
  Rocket,
  Link as LinkIcon,
  AlertTriangle,
  ArrowRightLeft,
} from "lucide-react";

import type { Withdrawal } from "@shared/schema";

/*
 * Validate Roblox gamepass URLs.
 *
 * Accepted format:
 * https://www.roblox.com/game-pass/123456/...
 */
const isValidGamepassUrl = (
  value: string
): boolean => {
  try {
    const url = new URL(value);

    const hostname =
      url.hostname.toLowerCase();

    if (
      hostname !== "roblox.com" &&
      hostname !== "www.roblox.com"
    ) {
      return false;
    }

    if (url.protocol !== "https:") {
      return false;
    }

    return /^\/game-pass\/\d+(?:\/|$)/i.test(
      url.pathname
    );
  } catch {
    return false;
  }
};

/*
 * Withdrawal form validation.
 *
 * robuxAmount is kept as a real number in the form instead
 * of converting it using parseInt().
 *
 * That prevents values such as:
 *
 * 5.9
 *
 * from silently turning into:
 *
 * 5
 */
const withdrawalSchema = z.object({
  gamepassUrl: z
    .string()
    .url(
      "Please enter a valid gamepass URL"
    )
    .refine(isValidGamepassUrl, {
      message:
        "Must be a valid Roblox gamepass URL",
    }),

  robuxAmount: z
    .number()
    .int(
      "Robux amount must be a whole number."
    )
    .min(5, {
      message:
        "Minimum withdrawal is 5 Robux.",
    }),
});

type WithdrawalForm =
  z.infer<typeof withdrawalSchema>;

type WithdrawalSubmitResponse = {
  id?: Withdrawal["id"];
  robuxAmount?: number;
  status?: string;
  message?: string;
  withdrawalId?: Withdrawal["id"];
  refunded?: boolean;

  [key: string]: unknown;
};

// ----------------------------------------------------
// Fetch settings
// ----------------------------------------------------

const fetchSettings = async () => {
  const res = await fetch(
    "/api/settings",
    {
      credentials: "include",
    }
  );

  if (!res.ok) {
    throw new Error(
      "Failed to fetch settings"
    );
  }

  return res.json();
};

// ----------------------------------------------------
// Fetch available Robux stock
// ----------------------------------------------------

const fetchRobuxStock =
  async (): Promise<number> => {
    const res = await fetch(
      "/api/robux-stock",
      {
        credentials: "include",
      }
    );

    /*
     * Your new backend intentionally returns 503
     * if the payout account cannot be verified.
     *
     * Do not convert that into a fake balance of 0.
     */
    if (!res.ok) {
      const errorData = await res
        .json()
        .catch(() => null);

      throw new Error(
        errorData?.message ??
        `Failed to fetch Robux stock (${res.status})`
      );
    }

    const data = await res.json();

    /*
     * Make sure the API actually returned a valid
     * numeric Robux balance.
     */
    if (
      typeof data?.robuxBalance !==
      "number" ||
      !Number.isFinite(
        data.robuxBalance
      ) ||
      data.robuxBalance < 0
    ) {
      throw new Error(
        "Server returned an invalid Robux balance."
      );
    }

    return data.robuxBalance;
  };

// ----------------------------------------------------
// Fetch user's withdrawal history
// ----------------------------------------------------

const fetchRecentWithdrawals =
  async (): Promise<Withdrawal[]> => {
    const res = await fetch(
      "/api/withdrawals",
      {
        credentials: "include",
      }
    );

    if (!res.ok) {
      if (res.status === 401) {
        return [];
      }

      throw new Error(
        "Failed to fetch withdrawals"
      );
    }

    const data = await res.json();

    return Array.isArray(data)
      ? data
      : [];
  };

// ----------------------------------------------------
// Submit withdrawal
// ----------------------------------------------------

const submitWithdrawal = async (
  data: WithdrawalForm
): Promise<WithdrawalSubmitResponse> => {
  const res = await fetch(
    "/api/withdraw",
    {
      method: "POST",

      credentials: "include",

      headers: {
        "Content-Type":
          "application/json",
      },

      body: JSON.stringify(data),
    }
  );

  /*
   * Read JSON whether the request succeeded
   * or failed.
   *
   * Your backend returns meaningful error
   * messages in JSON.
   */
  const responseData = await res
    .json()
    .catch(() => null);

  if (!res.ok) {
    throw new Error(
      responseData?.message ??
      `Withdrawal failed (${res.status})`
    );
  }

  if (
    !responseData ||
    typeof responseData !== "object"
  ) {
    throw new Error(
      "Server returned an invalid withdrawal response."
    );
  }

  return responseData;
};

// ----------------------------------------------------
// Timestamp helper
// ----------------------------------------------------

const toDate = (
  value: any
): Date => {
  if (!value) {
    return new Date();
  }

  if (
    typeof value?.toDate ===
    "function"
  ) {
    return value.toDate();
  }

  if (typeof value === "number") {
    /*
     * Seconds timestamp
     */
    if (value < 1e12) {
      return new Date(
        value * 1000
      );
    }

    /*
     * Milliseconds timestamp
     */
    return new Date(value);
  }

  const date =
    new Date(value);

  return Number.isNaN(
    date.getTime()
  )
    ? new Date()
    : date;
};

// ====================================================
// PAGE
// ====================================================

const WithdrawPage = () => {
  const [
    selectedAmount,
    setSelectedAmount,
  ] = useState<number | null>(
    null
  );

  const { user } = useAuth();

  const { toast } =
    useToast();

  const queryClient =
    useQueryClient();

  // --------------------------------------------------
  // Form
  // --------------------------------------------------

  const form =
    useForm<WithdrawalForm>({
      resolver:
        zodResolver(
          withdrawalSchema
        ),

      defaultValues: {
        gamepassUrl: "",
        robuxAmount: 5,
      },
    });

  // --------------------------------------------------
  // Public settings
  // --------------------------------------------------

  const {
    data: settings,
  } = useQuery({
    queryKey: [
      "publicSettings",
    ],

    queryFn:
      fetchSettings,

    staleTime:
      1000 * 60 * 5,

    refetchOnWindowFocus:
      false,
  });

  // --------------------------------------------------
  // Robux stock
  // --------------------------------------------------

  const {
    data: robuxStock,

    isError:
    isRobuxStockError,

    isFetching:
    isFetchingRobuxStock,
  } = useQuery<number>({
    queryKey: [
      "robuxStock",
    ],

    queryFn:
      fetchRobuxStock,

    /*
     * Refresh every 30 seconds.
     */
    refetchInterval:
      30_000,

    refetchOnWindowFocus:
      false,

    retry: 1,
  });

  // --------------------------------------------------
  // Withdrawal history
  // --------------------------------------------------

  const {
    data:
    rawWithdrawals,

    isLoading:
    isLoadingWithdrawals,
  } = useQuery<
    Withdrawal[]
  >({
    queryKey: [
      "recentWithdrawals",
      user?.id,
    ],

    queryFn:
      fetchRecentWithdrawals,

    enabled:
      !!user?.id,

    staleTime:
      1000 * 60,

    refetchOnWindowFocus:
      false,

    retry: 1,
  });

  // --------------------------------------------------
  // Withdrawal mutation
  // --------------------------------------------------

  const withdrawalMutation =
    useMutation({
      mutationFn:
        submitWithdrawal,

      onSuccess: (
        data
      ) => {
        /*
         * Backend normally includes robuxAmount,
         * but use the submitted form value as
         * a fallback.
         */
        const robuxAmountFromResponse =
          data.robuxAmount ??
          form.getValues(
            "robuxAmount"
          );

        const status =
          data.status;

        /*
         * Your new backend returns:
         *
         * completed
         *
         * when Roblox definitely confirmed the
         * purchase.
         *
         * It may return:
         *
         * pending
         *
         * when manual reconciliation is needed.
         */
        toast({
          title:
            status ===
              "completed"
              ? "Withdrawal Complete"
              : "Withdrawal Submitted",

          description:
            status ===
              "completed"
              ? `${robuxAmountFromResponse.toLocaleString()} Robux was successfully processed.`
              : data.message ??
              `Your ${robuxAmountFromResponse.toLocaleString()} Robux withdrawal is pending verification.`,
        });

        /*
         * Reset form.
         */
        form.reset({
          gamepassUrl:
            "",

          robuxAmount:
            5,
        });

        setSelectedAmount(
          null
        );

        /*
         * Refresh active application data.
         *
         * This refreshes:
         *
         * Robux stock
         * recent withdrawals
         * user/auth query if useAuth uses React Query
         *
         * This is useful because the user's token
         * balance changes after withdrawal.
         */
        void queryClient.invalidateQueries();
      },

      onError: (
        error: unknown
      ) => {
        const message =
          error instanceof Error
            ? error.message
            : String(
              error ??
              "Unknown error"
            );

        toast({
          title:
            "Withdrawal Failed",

          description:
            message,

          variant:
            "destructive",
        });
      },
    });

  // --------------------------------------------------
  // Derived state
  // --------------------------------------------------

  const exchangeRate =
    settings?.tokens_per_robux ??
    2;

  const withdrawalsEnabled =
    settings?.withdrawals_enabled ??
    true;

  const isBanned =
    user?.isBanned ??
    false;

  const userTokenBalance =
    user?.tokenBalance ??
    0;

  const watchedRobuxAmount =
    form.watch(
      "robuxAmount"
    );

  const quickAmounts = [
    {
      robux: 100,
      tokens:
        100 *
        exchangeRate,
    },

    {
      robux: 250,
      tokens:
        250 *
        exchangeRate,
    },

    {
      robux: 500,
      tokens:
        500 *
        exchangeRate,
    },

    {
      robux: 1000,
      tokens:
        1000 *
        exchangeRate,
    },
  ];

  // --------------------------------------------------
  // Recent withdrawals -> TransactionItem format
  // --------------------------------------------------

  const recentWithdrawals =
    (
      rawWithdrawals ??
      []
    ).map((w) => ({
      id: w.id,

      type:
        "withdrawal" as const,

      description:
        `${w.robuxAmount} Robux withdrawal`,

      amount: -(
        w.tokensDeducted ??
        (
          w.robuxAmount
            ? w.robuxAmount *
            exchangeRate
            : 0
        )
      ),

      /*
       * TransactionItem appears to expect:
       *
       * processing
       * completed
       * failed
       */
      status:
        w.status ===
          "completed"
          ? (
            "completed" as const
          )
          : w.status ===
            "failed"
            ? (
              "failed" as const
            )
            : (
              "processing" as const
            ),

      createdAt:
        toDate(
          (
            w as any
          ).createdAt
        ),
    }));

  /*
   * The submit button must stay disabled until
   * we have successfully verified current stock.
   */
  const payoutUnavailable =
    isRobuxStockError ||
    robuxStock ===
    undefined;

  // --------------------------------------------------
  // Form submit
  // --------------------------------------------------

  const onSubmit = (
    data: WithdrawalForm
  ) => {
    /*
     * Client checks are for UX.
     *
     * Your backend still performs all authoritative
     * checks, which is important.
     */

    if (isBanned) {
      toast({
        title:
          "Account Banned",

        description:
          "Your account is banned and cannot make withdrawals.",

        variant:
          "destructive",
      });

      return;
    }

    if (
      !withdrawalsEnabled
    ) {
      toast({
        title:
          "Withdrawals Disabled",

        description:
          "Withdrawals are currently disabled.",

        variant:
          "destructive",
      });

      return;
    }

    /*
     * Never submit while Roblox stock cannot be
     * verified.
     */
    if (
      isRobuxStockError ||
      robuxStock ===
      undefined
    ) {
      toast({
        title:
          "Robux Stock Unavailable",

        description:
          "The payout account could not be verified. Please try again later.",

        variant:
          "destructive",
      });

      return;
    }

    const totalTokensNeeded =
      data.robuxAmount *
      exchangeRate;

    /*
     * User token balance check.
     */
    if (
      totalTokensNeeded >
      userTokenBalance
    ) {
      toast({
        title:
          "Insufficient Balance",

        description:
          `You need ${totalTokensNeeded.toLocaleString()} tokens but only have ${userTokenBalance.toLocaleString()}.`,

        variant:
          "destructive",
      });

      return;
    }

    /*
     * Robux stock check.
     *
     * The backend performs this check again.
     */
    if (
      data.robuxAmount >
      robuxStock
    ) {
      toast({
        title:
          "Insufficient Robux Stock",

        description:
          `Only ${robuxStock.toLocaleString()} Robux is currently available.`,

        variant:
          "destructive",
      });

      return;
    }

    withdrawalMutation.mutate(
      data
    );
  };

  // --------------------------------------------------
  // Quick amount selector
  // --------------------------------------------------

  const selectAmount = (
    robux: number
  ) => {
    setSelectedAmount(
      robux
    );

    form.setValue(
      "robuxAmount",
      robux,
      {
        shouldValidate:
          true,

        shouldDirty:
          true,
      }
    );
  };

  // ==================================================
  // UI
  // ==================================================

  return (
    <div className="py-20 px-4">
      <div className="container mx-auto max-w-2xl">
        {/* PAGE HEADER */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
          }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center">
            <ArrowRightLeft className="w-8 h-8 mr-3 text-primary" />

            Convert to Robux
          </h1>

          <p className="text-xl text-muted-foreground">
            Exchange your
            earned tokens for
            Robux through
            automated gamepass
            purchases
          </p>
        </motion.div>

        {/* ROBUX STOCK */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            delay: 0.1,
          }}
          className="mb-8"
        >
          <Card className="glass-effect border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                    <Database className="w-6 h-6 text-accent" />
                  </div>

                  <div>
                    <div className="font-semibold">
                      Available
                      Robux Stock
                    </div>

                    <div className="text-sm text-muted-foreground">
                      Automatically
                      updated
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div
                    className="text-2xl font-bold text-accent"
                    data-testid="robux-stock"
                  >
                    {isRobuxStockError
                      ? "Unavailable"
                      : robuxStock?.toLocaleString() ??
                      "Loading..."}
                  </div>

                  <div className="text-sm text-muted-foreground">
                    {isRobuxStockError
                      ? "Could not verify payout account"
                      : isFetchingRobuxStock
                        ? "Updating..."
                        : "Robux available"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* WITHDRAWAL CARD */}

        <motion.div
          initial={{
            opacity: 0,
            y: 20,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{
            duration: 0.6,
            delay: 0.2,
          }}
        >
          <Card
            className={`glass-effect border-border ${!withdrawalsEnabled ||
                isBanned
                ? "bg-muted/30"
                : ""
              }`}
          >
            <CardHeader>
              <CardTitle>
                Withdrawal
                Request
              </CardTitle>
            </CardHeader>

            <CardContent>
              {/* ACCOUNT BANNED */}

              {isBanned && (
                <Alert
                  variant="destructive"
                  className="mb-6"
                >
                  <AlertTriangle className="h-4 w-4" />

                  <AlertTitle>
                    Account
                    Banned
                  </AlertTitle>

                  <AlertDescription>
                    Your account is
                    banned and
                    cannot make
                    withdrawals.
                  </AlertDescription>
                </Alert>
              )}

              {/* WITHDRAWALS DISABLED */}

              {!withdrawalsEnabled &&
                !isBanned && (
                  <Alert
                    variant="destructive"
                    className="mb-6"
                  >
                    <AlertTriangle className="h-4 w-4" />

                    <AlertTitle>
                      Withdrawals
                      Disabled
                    </AlertTitle>

                    <AlertDescription>
                      Withdrawals
                      are temporarily
                      disabled.
                      Please check
                      back later.
                    </AlertDescription>
                  </Alert>
                )}

              {/* ROBLOX PAYOUT ACCOUNT FAILURE */}

              {isRobuxStockError &&
                withdrawalsEnabled &&
                !isBanned && (
                  <Alert
                    variant="destructive"
                    className="mb-6"
                  >
                    <AlertTriangle className="h-4 w-4" />

                    <AlertTitle>
                      Robux
                      Payouts
                      Unavailable
                    </AlertTitle>

                    <AlertDescription>
                      We could not
                      verify the
                      Roblox payout
                      account.
                      Withdrawals
                      cannot be
                      processed
                      until the
                      connection is
                      restored.
                    </AlertDescription>
                  </Alert>
                )}

              <fieldset
                disabled={
                  !withdrawalsEnabled ||
                  isBanned
                }
              >
                <Form
                  {...form}
                >
                  <form
                    onSubmit={form.handleSubmit(
                      onSubmit
                    )}
                    className="space-y-6"
                  >
                    {/* TOKEN BALANCE */}

                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Wallet className="w-5 h-5 text-primary" />

                          <span className="font-medium">
                            Your
                            Balance
                          </span>
                        </div>

                        <div
                          className="text-lg font-bold text-primary"
                          data-testid="user-balance"
                        >
                          {userTokenBalance.toLocaleString()}{" "}
                          tokens
                        </div>
                      </div>
                    </div>

                    {/* GAMEPASS URL */}

                    <FormField
                      control={
                        form.control
                      }
                      name="gamepassUrl"
                      render={({
                        field,
                      }) => (
                        <FormItem>
                          <FormLabel>
                            Your
                            Gamepass
                            URL
                          </FormLabel>

                          <FormControl>
                            <div className="relative">
                              <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />

                              <Input
                                placeholder="https://www.roblox.com/game-pass/..."
                                className="pl-10"
                                autoComplete="off"
                                {...field}
                              />
                            </div>
                          </FormControl>

                          <Alert className="mt-2">
                            <Info className="h-4 w-4" />

                            <AlertDescription>
                              The
                              gamepass
                              must
                              belong
                              to your
                              verified
                              Roblox
                              account,
                              be on
                              sale,
                              and
                              match
                              the
                              requested
                              Robux
                              amount.
                            </AlertDescription>
                          </Alert>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* ROBUX AMOUNT */}

                    <FormField
                      control={
                        form.control
                      }
                      name="robuxAmount"
                      render={({
                        field,
                      }) => (
                        <FormItem>
                          <FormLabel>
                            Select
                            Amount
                          </FormLabel>

                          <FormControl>
                            <div className="space-y-4">
                              {/* QUICK AMOUNTS */}

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {quickAmounts.map(
                                  (
                                    amount
                                  ) => {
                                    /*
                                     * Disable quick buttons
                                     * that exceed current
                                     * payout stock.
                                     */
                                    const exceedsStock =
                                      robuxStock !==
                                      undefined &&
                                      amount.robux >
                                      robuxStock;

                                    return (
                                      <Button
                                        key={
                                          amount.robux
                                        }
                                        type="button"
                                        variant={
                                          selectedAmount ===
                                            amount.robux
                                            ? "default"
                                            : "outline"
                                        }
                                        className="p-3 h-auto text-center"
                                        onClick={() =>
                                          selectAmount(
                                            amount.robux
                                          )
                                        }
                                        disabled={
                                          exceedsStock
                                        }
                                      >
                                        <div>
                                          <div className="text-lg font-bold">
                                            {
                                              amount.robux
                                            }
                                          </div>

                                          <div className="text-xs opacity-80">
                                            Robux
                                          </div>

                                          <div className="text-xs text-muted-foreground">
                                            {amount.tokens.toLocaleString()}{" "}
                                            tokens
                                          </div>
                                        </div>
                                      </Button>
                                    );
                                  }
                                )}
                              </div>

                              {/* CUSTOM AMOUNT */}

                              <Input
                                type="number"
                                min={
                                  5
                                }
                                step={
                                  1
                                }
                                inputMode="numeric"
                                placeholder="Custom amount (Robux)"
                                name={
                                  field.name
                                }
                                ref={
                                  field.ref
                                }
                                onBlur={
                                  field.onBlur
                                }
                                value={
                                  field.value ??
                                  ""
                                }
                                onChange={(
                                  e
                                ) => {
                                  const rawValue =
                                    e
                                      .target
                                      .value;

                                  /*
                                   * Keep an empty input empty.
                                   */
                                  if (
                                    rawValue ===
                                    ""
                                  ) {
                                    field.onChange(
                                      undefined
                                    );

                                    setSelectedAmount(
                                      null
                                    );

                                    return;
                                  }

                                  /*
                                   * Convert the HTML input
                                   * string into an actual
                                   * number for Zod.
                                   */
                                  const value =
                                    Number(
                                      rawValue
                                    );

                                  field.onChange(
                                    value
                                  );

                                  setSelectedAmount(
                                    Number.isFinite(
                                      value
                                    ) &&
                                      value >
                                      0
                                      ? value
                                      : null
                                  );
                                }}
                              />
                            </div>
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* EXCHANGE RATE */}

                    <div className="p-4 rounded-lg bg-muted/20 border border-border">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          Exchange
                          Rate:
                        </span>

                        <span className="font-medium">
                          1 Robux ={" "}
                          {
                            exchangeRate
                          }{" "}
                          Tokens
                        </span>
                      </div>

                      {typeof watchedRobuxAmount ===
                        "number" &&
                        watchedRobuxAmount >
                        0 && (
                          <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-border">
                            <span className="text-muted-foreground">
                              Total
                              Cost:
                            </span>

                            <span className="font-bold text-primary">
                              {(
                                watchedRobuxAmount *
                                exchangeRate
                              ).toLocaleString()}{" "}
                              tokens
                            </span>
                          </div>
                        )}
                    </div>

                    {/* SUBMIT */}

                    <Button
                      type="submit"
                      className="w-full py-4 text-lg font-semibold glow-effect"
                      disabled={
                        form
                          .formState
                          .isSubmitting ||
                        withdrawalMutation.isPending ||
                        payoutUnavailable
                      }
                    >
                      {withdrawalMutation.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2" />

                          Processing...
                        </>
                      ) : (
                        <>
                          <Rocket className="w-5 h-5 mr-2" />

                          Process
                          Withdrawal
                        </>
                      )}
                    </Button>

                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">
                        Minimum
                        withdrawal:
                        5 Robux.
                      </p>
                    </div>
                  </form>
                </Form>
              </fieldset>
            </CardContent>
          </Card>
        </motion.div>

        {/* RECENT WITHDRAWALS */}

        {isLoadingWithdrawals && (
          <div className="text-center mt-8">
            Loading recent
            withdrawals...
          </div>
        )}

        {recentWithdrawals.length >
          0 && (
            <motion.div
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.6,
                delay: 0.4,
              }}
              className="mt-8"
            >
              <Card className="glass-effect border-border">
                <CardHeader>
                  <CardTitle>
                    Recent
                    Withdrawals
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {recentWithdrawals.map(
                      (
                        withdrawal,
                        index
                      ) => (
                        <motion.div
                          key={
                            withdrawal.id
                          }
                          initial={{
                            opacity: 0,
                            x: -20,
                          }}
                          animate={{
                            opacity: 1,
                            x: 0,
                          }}
                          transition={{
                            delay:
                              0.1 *
                              index,

                            duration:
                              0.4,
                          }}
                        >
                          <TransactionItem
                            {...withdrawal}
                          />
                        </motion.div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
      </div>
    </div>
  );
};

export default WithdrawPage;